import type * as React from 'react'

// Epic 52 Phase 02 — icon normalisation contract
// (.claude/epics/52-token-and-icon-foundation/reference/ICON-NORMALISATION.md §(b)/(c))
// on `icon / Info` (16px export at node 145:18120; 12px export at node 145:18109, not touched).
//
// NOTE (Phase 00 contract gap, still open): the contract's §(c) specifies a shared
// `SvgIconProps` type at `apps/web/src/components/icons/types.ts`. That file does not exist and
// no phase currently owns creating it. This component must not create that shared module
// unprompted, so the prop type is inlined here rather than imported, matching every other icon
// component shipped so far.
type SvgIconProps = Omit<React.SVGProps<SVGSVGElement>, 'children'> & {
  /** Accessible name. Omit for a purely decorative icon (default: aria-hidden). */
  title?: string
  /** Rendered size in px. Defaults to 16 — the artwork's native/authored size. */
  size?: 12 | 16
}

// D-ICON-1 (2026-08-25): the 12px and 16px `Info` exports were measured coordinate-by-coordinate
// (134 coordinates compared) and found to be a uniform 4/3 scale of one another, ratio spread
// 0.000007 — equal to within float rounding. Per the owner decision this single component keeps
// the 16px artwork and viewBox (more coordinate precision than the 12px export) and lets the
// `size` prop drive only the rendered width/height attributes; the viewBox stays "0 0 16 16"
// regardless of `size`, so the 12px render is the SVG user agent uniformly scaling this same
// path data by 12/16 — mathematically identical to what the separate 12px artwork was (the two
// differed only by that same uniform scale).
export function IconInfo({ title, size = 16, ...rest }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path
        d="M7.5 7C7.78983 7 8.06933 7.1006 8.29167 7.28255L8.38411 7.36589L8.46745 7.45833C8.6494 7.68067 8.75 7.96017 8.75 8.25V10.2956C9.04082 10.3988 9.25 10.6738 9.25 11C9.25 11.4142 8.91421 11.75 8.5 11.75C8.16848 11.75 7.85031 11.6185 7.61589 11.3841C7.38146 11.1497 7.25 10.8315 7.25 10.5V8.45312C6.95939 8.34979 6.75 8.07606 6.75 7.75C6.75 7.33579 7.08579 7 7.5 7Z"
        fill="currentColor"
      />
      <path
        d="M7.75 4.25C8.30228 4.25 8.75 4.69772 8.75 5.25C8.75 5.80228 8.30228 6.25 7.75 6.25C7.19772 6.25 6.75 5.80228 6.75 5.25C6.75 4.69772 7.19772 4.25 7.75 4.25Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 1.25C11.7279 1.25 14.75 4.27208 14.75 8C14.75 11.7279 11.7279 14.75 8 14.75C4.27208 14.75 1.25 11.7279 1.25 8C1.25 4.27208 4.27208 1.25 8 1.25ZM8 2.75C5.1005 2.75 2.75 5.1005 2.75 8C2.75 10.8995 5.1005 13.25 8 13.25C10.8995 13.25 13.25 10.8995 13.25 8C13.25 5.1005 10.8995 2.75 8 2.75Z"
        fill="currentColor"
      />
    </svg>
  )
}
